// Phase 7 · Package 7-C · Live voice presence taxonomy.
//
// Deterministic derivation of the five-state presence enum from
// the existing useLeviCommand internal state.
//
// Note: the `muted` state was intentionally retired after PO
// real-world QA (2026-02). Natural spoken endings + Pause + End
// covered the contractor need without an additional visible
// control. Safety-event mic release remains routed through
// endConversation (permission revoke, tab hide, drawer close,
// inactivity), not through mute.
//
// PRECEDENCE (highest → lowest). Mutually exclusive by construction —
// the UI can never simultaneously represent Levi as speaking and
// listening:
//
//   1. "ended"     — conversation loop closed (contractor deactivated
//                    OR tab hidden OR permission revoked OR drawer
//                    closed). Highest precedence.
//   2. "listening" — mic capturing, VAD armed (status === listening).
//   3. "thinking"  — server round-trip in flight
//                    (status is one of uploading | thinking |
//                     executing | awaiting_confirmation).
//   4. "speaking"  — Levi TTS actively playing (browser or server).
//   5. "standby"   — conversation active but Levi silent and mic
//                    idle. Contractor tap re-enters listening.
//
// PURE FUNCTION. No React state. No side effects. Safe to call
// from render paths.

export const PRESENCE_STATES = Object.freeze([
  "listening", "thinking", "speaking", "standby", "ended",
]);

const _THINKING_STATUSES = new Set([
  "uploading", "thinking", "executing", "awaiting_confirmation",
]);

export function derivePresence({
  conversationActive,
  drawerOpen,
  tabHidden,
  micPermissionDenied,
  isSpeaking,
  status,
} = {}) {
  // (1) ended — highest precedence.
  if (!conversationActive) return "ended";
  if (drawerOpen === false) return "ended";
  if (tabHidden === true) return "ended";
  if (micPermissionDenied === true) return "ended";
  // (2) listening.
  if (status === "listening") return "listening";
  // (3) thinking.
  if (status && _THINKING_STATUSES.has(status)) return "thinking";
  // (4) speaking.
  if (isSpeaking === true) return "speaking";
  // (5) standby (default when active but idle).
  return "standby";
}

// Bilingual UI copy for the presence badge. Callers pass the
// resolved presence string.
export const PRESENCE_LABELS = Object.freeze({
  en: {
    listening: "Listening",
    thinking: "Thinking",
    speaking: "Speaking",
    standby: "Standby",
    ended: "Ended",
  },
  es: {
    listening: "Escuchando",
    thinking: "Pensando",
    speaking: "Hablando",
    standby: "En espera",
    ended: "Terminado",
  },
});
